import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { supabase } from '@/lib/supabase';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { useAuth } from '@/hooks/useAuth';
import { Invitation } from '@/api/entities';
import { CheckCircle, XCircle, Clock, Mail } from 'lucide-react';

export default function InvitationAccept() {
  const [searchParams] = useSearchParams();
  const [invitation, setInvitation] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [isAccepting, setIsAccepting] = useState(false);
  const { user, signIn, signUp } = useAuth();
  const navigate = useNavigate();
  
  const token = searchParams.get('token');

  useEffect(() => {
    if (token) {
      loadInvitation();
    } else {
      setError('Invalid invitation link');
      setIsLoading(false);
    }
  }, [token]);

  const loadInvitation = async () => {
    try {
      const { data, error } = await supabase
        .from('invitations')
        .select(`
          *,
          university:universities(*),
          inviter:user_profiles(full_name, email)
        `)
        .eq('invitation_token', token)
        .eq('status', 'pending')
        .single();

      if (error) throw new Error('Invitation not found or expired');
      
      if (new Date(data.expires_at) < new Date()) {
        throw new Error('This invitation has expired');
      }

      setInvitation(data);
    } catch (err) {
      setError(err.message);
    }
    setIsLoading(false);
  };

  const handleAcceptInvitation = async () => {
    if (!invitation || !user) return;
    
    setIsAccepting(true);
    try {
      await Invitation.acceptInvitation(token, user.id);
      navigate('/dashboard');
    } catch (err) {
      setError(err.message);
    }
    setIsAccepting(false);
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-200 flex items-center justify-center">
        <Card className="neumorphic rounded-3xl max-w-md w-full">
          <CardContent className="p-8 text-center">
            <Clock className="w-12 h-12 mx-auto mb-4 text-blue-600 animate-spin" />
            <p>Loading invitation...</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-200 flex items-center justify-center">
        <Card className="neumorphic rounded-3xl max-w-md w-full">
          <CardContent className="p-8 text-center">
            <XCircle className="w-12 h-12 mx-auto mb-4 text-red-500" />
            <h2 className="text-xl font-bold text-neumorphic mb-4">Invalid Invitation</h2>
            <p className="text-gray-600 mb-6">{error}</p>
            <Button onClick={() => navigate('/login')} className="neumorphic rounded-xl">
              Go to Login
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="min-h-screen bg-gray-200 flex items-center justify-center">
        <Card className="neumorphic rounded-3xl max-w-md w-full">
          <CardHeader className="text-center">
            <Mail className="w-12 h-12 mx-auto mb-4 text-blue-600" />
            <CardTitle className="text-xl font-bold text-neumorphic">
              You're Invited to Join UniMentor
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <p className="text-sm text-blue-800">
                <strong>University:</strong> {invitation.university.name}<br/>
                <strong>Role:</strong> {invitation.role.replace('_', ' ').toUpperCase()}<br/>
                <strong>Invited by:</strong> {invitation.inviter.full_name}
              </p>
            </div>

            <Alert>
              <AlertDescription>
                Please sign in or create an account to accept this invitation.
              </AlertDescription>
            </Alert>

            <div className="flex gap-3">
              <Button
                onClick={() => navigate('/login')}
                className="flex-1 neumorphic rounded-xl"
              >
                Sign In
              </Button>
              <Button
                onClick={() => navigate('/login')}
                className="flex-1 neumorphic rounded-xl bg-gradient-to-r from-blue-500 to-cyan-600 text-white"
              >
                Create Account
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-200 flex items-center justify-center">
      <Card className="neumorphic rounded-3xl max-w-md w-full">
        <CardHeader className="text-center">
          <CheckCircle className="w-12 h-12 mx-auto mb-4 text-green-600" />
          <CardTitle className="text-xl font-bold text-neumorphic">
            Accept Invitation
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="bg-green-50 border border-green-200 rounded-lg p-4">
            <p className="text-sm text-green-800">
              <strong>University:</strong> {invitation.university.name}<br/>
              <strong>Role:</strong> {invitation.role.replace('_', ' ').toUpperCase()}<br/>
              <strong>Invited by:</strong> {invitation.inviter.full_name}
            </p>
          </div>
          
          <Button 
            onClick={handleAcceptInvitation}
            disabled={isAccepting}
            className="w-full neumorphic rounded-xl py-3 bg-gradient-to-r from-green-500 to-emerald-600 text-white"
          >
            {isAccepting ? 'Accepting...' : 'Accept Invitation'}
          </Button>
        </CardContent>
      </Card>
    </div>
  );
}